import { ServiceHistoryStatus, VehicleUsage, VehicleValuationType } from "@/api-schema-enums";
import type { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import { inputStringToDate } from "@/common/helpers/date-input";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { getVehicleVinValidation } from "@/common/helpers/vehicle-vin";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCard, FormCardActions, FormCardSection } from "@/components/ui/form-card";
import { Input, InputTextAlign } from "@/components/ui/input";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { RadioGroup, RadioGroupCardItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VehicleInfoCard } from "@/components/vehicle-info-card/vehicle-info-card";
import {
  getVehicleValuationTargetDateBounds,
  isVehicleValuationTargetDateAllowed,
  toLocalIsoDate,
} from "@/components/vehicle-valuation/valuation-date";
import { VehicleValuationDateIndicator } from "@/components/vehicle-valuation/valuation-date-indicator";
import { VehicleVinFieldStatus } from "@/components/vehicle-vin/vehicle-vin-field-status";
import { zodResolver } from "@hookform/resolvers/zod";
import { InfoIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VehicleValuationTypeHintPopover } from "../../_components/vehicle-valuation-type-hint-popover";
import {
  getVehicleAccidentStateOptions,
  getVehicleValuationRimTypeOptions,
  getVehicleValuationTypeOptions,
  getVehicleValuationTypeSummary,
  getVehicleVatDeductibleSelectOptions,
  isVehicleAccidentState,
  shouldShowVehicleAccidentDetails,
  VEHICLE_VALUATION_CREATE_TYPE_VALUES,
} from "../../_helpers/vehicle-valuation-metadata";
import type { VehicleValuationCreateFormValues } from "../_helpers/vehicle-valuation-create-form.helpers";
import {
  getMinRegistrationDate,
  getVehicleValuationCreateFormDefaultValues,
  getVehicleValuationCreateFormInitializationKey,
  getVehicleValuationCreateFormSchema,
  type VehicleValuationCreateFormInputValues,
} from "../_helpers/vehicle-valuation-create-form.helpers";

const FORM_SET_VALUE_OPTIONS = { shouldValidate: true } as const;
export type { VehicleValuationCreateFormValues } from "../_helpers/vehicle-valuation-create-form.helpers";

interface VehicleValuationCreateFormFieldsProps {
  vehicle?: VehicleWithRegistrationDate | null;
  defaultVin?: string;
  requireRegistrationDate?: boolean;
  showVinField?: boolean;
  vinHint?: string;
  vinReadOnly?: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (values: VehicleValuationCreateFormValues) => Promise<void> | void;
}

export function VehicleValuationCreateFormFields({
  vehicle,
  defaultVin,
  requireRegistrationDate = false,
  showVinField = false,
  vinHint,
  vinReadOnly = false,
  isPending,
  onCancel,
  onSubmit,
}: VehicleValuationCreateFormFieldsProps) {
  const { t } = useTranslation(["common", "fab", "masterdata"]);
  const targetDateBounds = useMemo(() => getVehicleValuationTargetDateBounds(), []);
  const minRegistrationDate = useMemo(() => getMinRegistrationDate(), []);
  const formSchema = useMemo(
    () => getVehicleValuationCreateFormSchema(t, showVinField, requireRegistrationDate),
    [requireRegistrationDate, showVinField, t],
  );
  const accidentStateOptions = useMemo(() => getVehicleAccidentStateOptions(t), [t]);
  const rimTypeOptions = useMemo(() => getVehicleValuationRimTypeOptions(t), [t]);
  const valuationTypeOptions = useMemo(
    () => getVehicleValuationTypeOptions(t, VEHICLE_VALUATION_CREATE_TYPE_VALUES),
    [t],
  );
  const vatDeductibleOptions = useMemo(() => getVehicleVatDeductibleSelectOptions(t), [t]);
  const vehicleUsageOptions = useMemo(
    () => [
      { value: VehicleUsage.UNKNOWN, label: t("masterdata:VehicleUsageUnknown") },
      { value: VehicleUsage.RegularPersonalCommercial, label: t("masterdata:VehicleUsageRegular") },
      { value: VehicleUsage.RENTAL, label: t("masterdata:VehicleUsageRental") },
      { value: VehicleUsage.TaxiRideHailing, label: t("masterdata:VehicleUsageTaxi") },
      { value: VehicleUsage.DrivingSchool, label: t("masterdata:VehicleUsageDrivingSchool") },
      { value: VehicleUsage.DEMO, label: t("masterdata:VehicleUsageDemo") },
      { value: VehicleUsage.FleetPool, label: t("masterdata:VehicleUsageFleetPool") },
      { value: VehicleUsage.DeliveryCourier, label: t("masterdata:VehicleUsageDelivery") },
      { value: VehicleUsage.SpecialPurpose, label: t("masterdata:VehicleUsageSpecialPurpose") },
      { value: VehicleUsage.CONSTRUCTION, label: t("masterdata:VehicleUsageConstruction") },
      { value: VehicleUsage.ManagementCompanyCar, label: t("masterdata:VehicleUsageManagementCompanyCar") },
      { value: VehicleUsage.EmployeeCompanyCar, label: t("masterdata:VehicleUsageEmployeeCompanyCar") },
    ],
    [t],
  );
  const serviceHistoryOptions = useMemo(
    () => [
      { value: ServiceHistoryStatus.UNKNOWN, label: t("masterdata:ServiceHistoryUnknown") },
      { value: ServiceHistoryStatus.NONE, label: t("masterdata:ServiceHistoryNone") },
      { value: ServiceHistoryStatus.PARTIAL, label: t("masterdata:ServiceHistoryPartial") },
      { value: ServiceHistoryStatus.FullServiceHistory, label: t("masterdata:ServiceHistoryFull") },
      { value: ServiceHistoryStatus.FullDealerServiceHistory, label: t("masterdata:ServiceHistoryFullDealer") },
    ],
    [t],
  );
  const targetDateHint = useMemo(
    () =>
      t("fab:VehicleValuationTargetDateHint", {
        startDate: formatDateToLocale(toLocalIsoDate(targetDateBounds.firstFutureDate), { dateOnly: true }),
        endDate: formatDateToLocale(toLocalIsoDate(targetDateBounds.maxDate), { dateOnly: true }),
      }),
    [t, targetDateBounds.firstFutureDate, targetDateBounds.maxDate],
  );
  const initialFormValues = useMemo(
    () => getVehicleValuationCreateFormDefaultValues({ defaultVin, vehicle }),
    [defaultVin, vehicle],
  );
  const initializationKey = useMemo(
    () => getVehicleValuationCreateFormInitializationKey({ defaultVin, vehicle }),
    [defaultVin, vehicle],
  );
  const initializationKeyRef = useRef<string | null>(null);

  function handleSubmit(values: VehicleValuationCreateFormInputValues): Promise<void> | void {
    return onSubmit(formSchema.parse(values));
  }

  const form = useForm<VehicleValuationCreateFormInputValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema, undefined, { raw: true }),
    defaultValues: initialFormValues,
  });
  const accidentStateValue = useWatch({ control: form.control, name: "accidentState" });
  const earlyLeaseTerminationValue = useWatch({ control: form.control, name: "earlyLeaseTermination" });
  const exchangeEngineInstalledValue = useWatch({ control: form.control, name: "exchangeEngineInstalled" });
  const valuationDateValue = useWatch({ control: form.control, name: "valuationDate" });
  const vinValue = useWatch({ control: form.control, name: "vin" });
  const valuationDateIndicatorValue = useMemo(() => inputStringToDate(valuationDateValue ?? ""), [valuationDateValue]);
  const { isSubmitted, isValid, touchedFields } = form.formState;
  const showRegistrationDateError = !!touchedFields.registrationDate || isSubmitted;
  const showHuDateError = !!touchedFields.huDate || isSubmitted;
  const showValuationDateError = !!touchedFields.valuationDate || isSubmitted;
  const vinLengthDone = !!touchedFields.vin || isSubmitted;
  const vinValidation = getVehicleVinValidation(vinValue ?? "", vinLengthDone);
  const shouldEnableAccidentDetails =
    isVehicleAccidentState(accidentStateValue) && shouldShowVehicleAccidentDetails(accidentStateValue);
  const shouldEnableEarlyLeaseTerminationAmount = earlyLeaseTerminationValue === true;

  useEffect(() => {
    if (initializationKeyRef.current === initializationKey) {
      return;
    }

    form.reset(initialFormValues, { keepDirtyValues: true });
    initializationKeyRef.current = initializationKey;
  }, [form, initialFormValues, initializationKey]);

  useEffect(() => {
    if (shouldEnableEarlyLeaseTerminationAmount || !form.getValues("earlyLeaseTerminationAmount")) {
      return;
    }

    form.setValue("earlyLeaseTerminationAmount", "", FORM_SET_VALUE_OPTIONS);
  }, [form, shouldEnableEarlyLeaseTerminationAmount]);

  useEffect(() => {
    if (shouldEnableAccidentDetails || (!form.getValues("accidentDescription") && !form.getValues("accidentValue"))) {
      return;
    }

    form.setValue("accidentDescription", "", FORM_SET_VALUE_OPTIONS);
    form.setValue("accidentValue", "", FORM_SET_VALUE_OPTIONS);
  }, [form, shouldEnableAccidentDetails]);

  const formContent = (
    <div className="flex flex-col gap-6">
      {vehicle ? <VehicleInfoCard vehicle={vehicle} /> : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormCard title={t("fab:VehicleValuationInputs")}>
            <div className="space-y-4">
              {showVinField ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="vin"
                    render={({ field, fieldState }) => {
                      const isVinEmpty = (field.value ?? "").trim().length === 0;
                      const showVinRequiredError = vinLengthDone && isVinEmpty && !!fieldState.error;
                      const showVinError = vinValidation.hasError || showVinRequiredError;

                      return (
                        <FormItem className="md:col-span-2">
                          <FormLabel required showError={showVinError}>
                            {t("fab:VIN")}
                          </FormLabel>
                          <FormControl showError={showVinError}>
                            <Input
                              placeholder={t("masterdata:VehicleVinPlaceholder")}
                              {...field}
                              maxLength={17}
                              readOnly={vinReadOnly}
                              disabled={vinReadOnly}
                              error={!vinReadOnly && showVinError}
                              value={field.value ?? ""}
                              onChange={
                                vinReadOnly
                                  ? undefined
                                  : (event) => {
                                      field.onChange(event.target.value.toUpperCase());
                                    }
                              }
                            />
                          </FormControl>
                          {vinReadOnly ? null : (
                            <VehicleVinFieldStatus value={vinValue ?? ""} showLengthError={vinLengthDone} />
                          )}
                          {showVinRequiredError ? <FormMessage /> : null}
                          {vinHint ? <p className="text-muted-foreground text-sm">{vinHint}</p> : null}
                        </FormItem>
                      );
                    }}
                  />
                </div>
              ) : null}

              <FormCardSection title={t("fab:VehicleValuationSetupSectionTitle")} contentClassName="grid gap-3">
                <FormField
                  control={form.control}
                  name="valuationType"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel required className="mb-0">
                          {t("fab:VehicleValuationType")}
                        </FormLabel>
                        <VehicleValuationTypeHintPopover />
                      </div>
                      <FormControl>
                        <RadioGroup
                          value={field.value ?? VehicleValuationType.Default}
                          onValueChange={field.onChange}
                          className="grid gap-3 md:grid-cols-2"
                        >
                          {valuationTypeOptions.map((option) => {
                            const selectedValue = field.value ?? VehicleValuationType.Default;
                            const isSelected = selectedValue === option.value;
                            const optionSummary = getVehicleValuationTypeSummary(option.value, t);
                            const optionId = `${field.name}-${option.value}`;

                            return (
                              <RadioGroupCardItem
                                key={option.value}
                                id={optionId}
                                value={option.value}
                                selected={isSelected}
                                label={option.label}
                                description={optionSummary}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border-border mt-1 grid gap-x-4 gap-y-3 border-t pt-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="earlyLeaseTermination"
                    render={({ field }) => (
                      <FormItem className="flex min-h-9 flex-row items-center space-y-0 space-x-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                          />
                        </FormControl>

                        <div className="flex items-center gap-2 leading-none">
                          <FormLabel className="mb-0">{t("fab:EarlyLeaseTermination")}</FormLabel>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label={t("fab:EarlyLeaseTerminationHintAriaLabel")}
                                className="text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-sm transition-colors"
                              >
                                <InfoIcon className="size-3.5" />
                              </button>
                            </TooltipTrigger>

                            <TooltipContent className="max-w-80">{t("fab:EarlyLeaseTerminationHint")}</TooltipContent>
                          </Tooltip>
                        </div>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="earlyLeaseTerminationAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required={shouldEnableEarlyLeaseTerminationAmount}>
                          {t("fab:EarlyLeaseTerminationAmount")}
                        </FormLabel>
                        <FormControl>
                          <LocalizedNumberInput
                            min={0}
                            inputMode="decimal"
                            prefix="€"
                            textAlign={InputTextAlign.Right}
                            name={field.name}
                            ref={field.ref}
                            value={field.value ?? ""}
                            onBlur={field.onBlur}
                            onValueChange={field.onChange}
                            disabled={!shouldEnableEarlyLeaseTerminationAmount}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormCardSection>

              <FormCardSection
                title={t("fab:VehicleValuationCalculationSectionTitle")}
                contentClassName="grid gap-x-4 gap-y-3 md:grid-cols-2"
              >
                <FormField
                  control={form.control}
                  name="registrationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={requireRegistrationDate} showError={showRegistrationDateError}>
                        {t("common:RegistrationDate")}
                      </FormLabel>
                      <FormControl showError={showRegistrationDateError}>
                        <DatePickerInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value ?? ""}
                          onChange={(value) =>
                            form.setValue("registrationDate", value, { shouldValidate: true, shouldDirty: true })
                          }
                          onBlur={() => {
                            field.onBlur();
                            void form.trigger("registrationDate");
                          }}
                          disabledDates={(date) => date < minRegistrationDate}
                          futureDaysDisabled
                        />
                      </FormControl>
                      <p className="text-muted-foreground text-sm">{t("fab:VehicleValuationMaxVehicleAgeHint")}</p>
                      <FormMessage showError={showRegistrationDateError} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="valuationDate"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel required showError={showValuationDateError} className="mb-0">
                          {t("fab:VehicleValuationTargetDate")}
                        </FormLabel>
                        <VehicleValuationDateIndicator value={valuationDateIndicatorValue} reserveSpace />
                      </div>
                      <FormControl showError={showValuationDateError}>
                        <DatePickerInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value ?? ""}
                          onChange={(value) =>
                            form.setValue("valuationDate", value, { shouldValidate: true, shouldDirty: true })
                          }
                          onBlur={() => {
                            field.onBlur();
                            void form.trigger("valuationDate");
                          }}
                          disabledDates={(date) => !isVehicleValuationTargetDateAllowed(date)}
                        />
                      </FormControl>
                      <p className="text-muted-foreground text-sm">{targetDateHint}</p>
                      <FormMessage showError={showValuationDateError} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="huDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel showError={showHuDateError}>{t("masterdata:HuDate")}</FormLabel>
                      <FormControl showError={showHuDateError}>
                        <DatePickerInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value ?? ""}
                          onChange={(value) =>
                            form.setValue("huDate", value, { shouldValidate: true, shouldDirty: true })
                          }
                          onBlur={() => {
                            field.onBlur();
                            void form.trigger("huDate");
                          }}
                          futureDaysDisabled
                        />
                      </FormControl>
                      <FormMessage showError={showHuDateError} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mileage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("masterdata:Mileage")}</FormLabel>
                      <FormControl>
                        <LocalizedNumberInput
                          min={0}
                          integerOnly
                          textAlign={InputTextAlign.Right}
                          name={field.name}
                          ref={field.ref}
                          value={field.value ?? ""}
                          onBlur={field.onBlur}
                          onValueChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numberOfHolders"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("masterdata:NumberOfHolders")}</FormLabel>
                      <FormControl>
                        <LocalizedNumberInput
                          min={0}
                          max={15}
                          integerOnly
                          textAlign={InputTextAlign.Right}
                          name={field.name}
                          ref={field.ref}
                          value={field.value ?? ""}
                          onBlur={field.onBlur}
                          onValueChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCardSection>

              <FormCardSection
                title={t("fab:VehicleValuationVehicleDetailsSectionTitle")}
                contentClassName="grid gap-x-4 gap-y-3 md:grid-cols-2"
              >
                <FormField
                  control={form.control}
                  name="rimType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("fab:RimType")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("fab:SelectRimType")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {rimTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="vatDeductible"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("masterdata:VatDeductible")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("masterdata:SelectVatDeductible")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {vatDeductibleOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vehicleUsage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("masterdata:VehicleUsage")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("masterdata:SelectVehicleUsage")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {vehicleUsageOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="serviceHistory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:ServiceHistory")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("masterdata:SelectServiceHistory")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {serviceHistoryOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCardSection>

              <FormCardSection
                title={t("fab:VehicleValuationAccidentSectionTitle")}
                contentClassName="grid gap-x-4 gap-y-3 md:grid-cols-[minmax(0,1fr)_minmax(0,220px)]"
              >
                <FormField
                  control={form.control}
                  name="accidentState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t("common:State")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ""}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("masterdata:SelectAccidentState")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accidentStateOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accidentValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={shouldEnableAccidentDetails}>{t("common:Value")}</FormLabel>
                      <FormControl>
                        <LocalizedNumberInput
                          min={0}
                          inputMode="decimal"
                          prefix="€"
                          textAlign={InputTextAlign.Right}
                          name={field.name}
                          ref={field.ref}
                          value={field.value ?? ""}
                          onBlur={field.onBlur}
                          onValueChange={field.onChange}
                          disabled={!shouldEnableAccidentDetails}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accidentDescription"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel required={shouldEnableAccidentDetails}>{t("common:Description")}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value ?? ""}
                          className="min-h-28"
                          placeholder={t("fab:AccidentDescriptionPlaceholder")}
                          rows={4}
                          disabled={!shouldEnableAccidentDetails}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCardSection>

              <FormCardSection
                title={t("masterdata:ExchangeEngine")}
                contentClassName="grid gap-x-4 gap-y-3 md:grid-cols-2"
              >
                <FormField
                  control={form.control}
                  name="exchangeEngineInstalled"
                  render={({ field }) => (
                    <FormItem className="flex min-h-9 flex-row items-center space-y-0 space-x-3 md:col-span-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          disabled={!!vehicle?.exchangeEngine}
                          onCheckedChange={(checked) =>
                            form.setValue("exchangeEngineInstalled", checked === true, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }
                        />
                      </FormControl>
                      <FormLabel className="mb-0">{t("masterdata:ExchangeEngine")}</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="exchangeDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={exchangeEngineInstalledValue}>{t("masterdata:ExchangeDate")}</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={(value) =>
                            form.setValue("exchangeDate", value, { shouldValidate: true, shouldDirty: true })
                          }
                          onBlur={field.onBlur}
                          futureDaysDisabled
                          disabled={!exchangeEngineInstalledValue}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vehicleMileageAtExchange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={exchangeEngineInstalledValue}>
                        {t("masterdata:VehicleMileageAtExchange")}
                      </FormLabel>
                      <FormControl>
                        <LocalizedNumberInput
                          min={0}
                          integerOnly
                          textAlign={InputTextAlign.Right}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onBlur={field.onBlur}
                          onValueChange={field.onChange}
                          disabled={!exchangeEngineInstalledValue}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="engineMileageAtExchange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:EngineMileageAtExchange")}</FormLabel>
                      <FormControl>
                        <LocalizedNumberInput
                          min={0}
                          integerOnly
                          textAlign={InputTextAlign.Right}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onBlur={field.onBlur}
                          onValueChange={field.onChange}
                          disabled={!exchangeEngineInstalledValue}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="exchangeNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:ExchangeNotes")}</FormLabel>
                      <FormControl>
                        <Textarea {...field} value={field.value} rows={3} disabled={!exchangeEngineInstalledValue} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCardSection>

              <FormCardSection title={t("fab:VehicleValuationCreatorNote")} contentClassName="grid gap-3">
                <FormField
                  control={form.control}
                  name="note"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("fab:VehicleValuationCreatorNoteOptional")}</FormLabel>
                      <FormControl>
                        <Textarea {...field} value={field.value ?? ""} className="min-h-28" rows={4} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCardSection>
            </div>
          </FormCard>

          <FormCardActions
            cancelLabel={t("common:Cancel")}
            submitLabel={t("fab:CreateVehicleValuation")}
            onCancel={onCancel}
            isPending={isPending}
            submitDisabled={isPending || !isValid}
          />
        </form>
      </Form>
    </div>
  );

  return <TooltipProvider>{formContent}</TooltipProvider>;
}
